/**
 * app/screens/HomeScreen.tsx
 * ---------------------------
 * What: Landing screen showing key widgets (stats, subscription status, notices).
 * Why: Provides a quick overview and entry points into the rest of the app.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Animated,
  Platform,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useAuth } from '../../lib/AuthContext';
// AsyncStorage no longer used for the email verification modal
import SubscriptionStatus from '../components/SubscriptionStatus';
import Stats from '../components/Stats';
import GeneralNotification from '../components/GeneralNotification';
import { useSubscription } from '../../lib/SubscriptionContext';
import { useFocusEffect } from '@react-navigation/native';
import { useData } from '../../lib/DataContext';
import { useTheme } from '../../lib/ThemeContext';
import { useConfig } from '../../lib/ConfigContext';
import { Ionicons } from '@expo/vector-icons';
import ThemedButton from '../components/ThemedButton';
import { supabase } from '../../lib/supabase';

// Try to import navigation hooks, but provide fallbacks if not in a navigator
let useNavigation, useRoute;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const nav = require('@react-navigation/native');
  useNavigation = nav.useNavigation;
  useRoute = nav.useRoute;
} catch {
  useNavigation = undefined;
  useRoute = undefined;
}

export default function HomeScreen(props: any) {
  const navigatorContext = props.navigatorContext ?? false;
  let navigation: any, route: any;
  if (navigatorContext) {
    const nav = require('@react-navigation/native');
    navigation = nav.useNavigation();
    route = nav.useRoute();
  }
  const { tier, refreshCounts, isLoading } = useSubscription();
  const { user } = useAuth();
  const { refreshAll } = useData();
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('Room updated successfully!');
  const fadeAnim = React.useRef(new Animated.Value(0)).current;
  const { theme, themeType } = useTheme();
  const glowStyle = themeType === 'blueNeon'
    ? (Platform.OS === 'web'
        ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
        : {
            shadowColor: theme.accent,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.5,
            shadowRadius: 6,
            elevation: 6,
          })
    : {};
  const { config } = useConfig();
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);

  // Helper to format date/time strings – define early as it's used in card construction below
  const formatDateTime = (iso?: string) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  const pm = config.plannedMaintenance || { enabled: false };
  const gn = config.generalNotification || { enabled: false, message: '' };

  // Build list of card elements with ordering from remote config
  const cards: { key: string; order: number; element: React.ReactElement }[] = [];

  // Helper to safely access order from config with fallback
  const getOrder = (key: keyof typeof config.HomeScreen): number => {
    return config.HomeScreen?.[key]?.order ?? 0;
  };

  // Maintenance notification card
  if (pm.enabled) {
    const maintenanceElement = (
      <View key="maintenance" style={[styles.maintenanceCard, { backgroundColor: theme.card, borderColor: theme.border }, glowStyle]}> 
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
          <Ionicons name="alert-circle" size={18} color={theme.error} style={{ marginRight: 6 }} />
          <Text style={[styles.maintenanceTitle, { color: theme.error }]}>Planned Maintenance</Text>
          <Ionicons name="alert-circle" size={18} color={theme.error} style={{ marginLeft: 6 }} />
        </View>
        <Text style={[styles.maintenanceMessage, { color: theme.secondaryText }]}>Log My Home will be offline for maintenance from {formatDateTime(pm.from)} to {formatDateTime(pm.to)}.{"\n"}{"\n"}We're just doing a quick tune-up to keep things running smoothly. Thanks for your patience!</Text>
      </View>
    );
    cards.push({ key: 'maintenance', order: getOrder('maintenance'), element: maintenanceElement });
  }

  // General notification card (free text)
  if (gn.enabled && gn.message) {
    cards.push({ key: 'generalNotification', order: getOrder('generalNotification'), element: <GeneralNotification key="generalNotification" message={gn.message} /> });
  }

  // Subscription status card (only for free tier)
  if (tier !== 'premium') {
    cards.push({ key: 'subscriptionStatus', order: getOrder('subscriptionStatus'), element: <SubscriptionStatus key="subscriptionStatus" /> });
  }

  // Stats card (always visible)
  cards.push({ key: 'stats', order: getOrder('stats'), element: <Stats key="stats" /> });

  // Sort cards by order ascending, undefined orders pushed to end
  cards.sort((a, b) => {
    const ao = typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
    const bo = typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
    return ao - bo;
  });

  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    Animated.sequence([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.delay(2000),
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]).start(() => {
      setShowSuccess(false);
    });
  };

  useEffect(() => {
    if (route && route.params?.showSuccess) {
      showSuccessMessage(route.params.message || 'Room updated successfully!');
      navigation?.setParams?.({ showSuccess: undefined, message: undefined });
    }
  }, [route?.params?.showSuccess]);

  useFocusEffect(
    React.useCallback(() => {
      refreshAll();
      refreshCounts();
    }, [refreshAll, refreshCounts])
  );

  // Show a one-time welcome modal after email verification (persist flag in auth metadata)
  useEffect(() => {
    (async () => {
      try {
        if (!user?.id || !user?.email_confirmed_at) return;
        // Fetch the freshest user so metadata is up to date across sessions
        const { data, error } = await supabase.auth.getUser();
        if (error) {
          console.warn('[HomeScreen] getUser error:', error);
        }
        const latestUser = data?.user ?? user;
        const alreadyShown = (latestUser.user_metadata as any)?.email_verification_welcome_shown === true;
        if (alreadyShown) return;

        setShowVerifiedModal(true);
        // Persist on the auth user so it never shows again across devices
        await supabase.auth.updateUser({
          data: { email_verification_welcome_shown: true },
        });
      } catch (err) {
        console.warn('[HomeScreen] Failed to persist verification welcome flag:', err);
      }
    })();
  }, [user?.id, user?.email_confirmed_at]);

  if (isLoading || !tier) {
    return (
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <ActivityIndicator size="large" color={theme.success} style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }} />
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      {showSuccess && (
        <Animated.View style={[styles.successMessage, { backgroundColor: theme.success }]}>
          <View style={styles.successMessageContent}>
            <Text style={[styles.successText, { color: theme.buttonText }]}>{successMessage}</Text>
          </View>
        </Animated.View>
      )}
      {showVerifiedModal && (
        <Modal
          visible={showVerifiedModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowVerifiedModal(false)}
        >
          <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}>
            <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
              <Text style={[styles.modalTitle, { color: theme.text }]}>Thank You</Text>
              <Text style={[styles.modalMessage, { color: theme.text, marginBottom: 20, textAlign:'center' }]}>Thank you for verifying your email address.{'\n'}Welcome to Log My Home</Text>
              <ThemedButton title="OK" onPress={() => setShowVerifiedModal(false)} style={{ minWidth: 140, alignSelf: 'center' }} />
            </View>
          </View>
        </Modal>
      )}
      <ScrollView contentContainerStyle={{ flexGrow: 1, paddingTop: 20, paddingBottom: 20, paddingHorizontal: 16 }}>
        <View style={{ padding: 20 }}>
          {cards.map((c) => c.element)}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  successMessage: {
    position: 'absolute',
    top: Platform.OS === 'web' ? 20 : 60,
    left: 20,
    right: 20,
    padding: 16,
    borderRadius: 8,
    zIndex: 9999,
  },
  successMessageContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  successText: {
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
  },
  maintenanceCard: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  maintenanceTitle: {
    fontSize: 17,
    fontWeight: '500',
  },
  maintenanceMessage: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#2a2a2a',
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: '500',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalMessage: {
    fontSize: 16,
    textAlign: 'center',
  },
}); 